import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { useEffect } from "react";
import "./App.css";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import RedeemAccessID from "./pages/RedeemAccessID";
import VerifyEmail from "./pages/VerifyEmail";
import CaseLibrary from "./pages/CaseLibrary";
import CaseReader from "./pages/CaseReader";
import Subscription from "./pages/Subscription";
import PaymentSuccess from "./pages/PaymentSuccess";
import Videos from "./pages/Videos";
import Research from "./pages/Research";
import ResearchArticle from "./pages/ResearchArticle";
import Subscribers from "./pages/Subscribers";
import Search from "./pages/Search";
import Simulations from "./pages/Simulations";
import SimulationPlayer from "./pages/SimulationPlayer";
import About from "./pages/About";
import Team from "./pages/Team";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import AdminDashboard from "./pages/AdminDashboard";
import AdminPricing from "./pages/AdminPricing";
import UserDashboard from "./pages/UserDashboard";
import InteractiveWorksheet from "./pages/InteractiveWorksheet";
import InstructorDashboard from "./pages/InstructorDashboard";
import InstructorSubmissions from "./pages/InstructorSubmissions";
import InstitutionDashboard from "./pages/InstitutionDashboard";
import BookTraining from "./pages/BookTraining";
import RequestCase from "./pages/RequestCase";

const ProtectedRoute = ({ children, adminOnly = false, instructorOnly = false }) => {
  const { user, loading } = useAuth();

  const isUnauthorized = !loading && user && (
    (adminOnly && user.role !== "admin") ||
    (instructorOnly && !["instructor", "admin", "institution_admin"].includes(user.role))
  );

  useEffect(() => {
    if (isUnauthorized) {
      toast.error("You are not authorised to access this feature. This is available only to Instructors and Institution Admins.");
    }
  }, [isUnauthorized]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (isUnauthorized) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/redeem" element={<RedeemAccessID />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/about" element={<About />} />
      <Route path="/team" element={<Team />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/videos" element={<Videos />} />
      <Route path="/research" element={<Research />} />
      <Route path="/research/:id" element={<ResearchArticle />} />
      <Route path="/subscribers" element={<Subscribers />} />
      <Route path="/search" element={<Search />} />
      <Route path="/simulations" element={<Simulations />} />
      <Route path="/simulations/:id/play" element={<ProtectedRoute><SimulationPlayer /></ProtectedRoute>} />
      
      <Route
        path="/cases"
        element={
          <ProtectedRoute>
            <CaseLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases/:caseId"
        element={
          <ProtectedRoute>
            <CaseReader />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worksheet/:caseId"
        element={
          <ProtectedRoute>
            <InteractiveWorksheet />
          </ProtectedRoute>
        }
      />
      <Route path="/subscription" element={<Subscription />} />
      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <UserDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly={true}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pricing"
        element={
          <ProtectedRoute adminOnly={true}>
            <AdminPricing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/instructor"
        element={
          <ProtectedRoute instructorOnly={true}>
            <InstructorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/instructor/submissions/:assignmentId"
        element={
          <ProtectedRoute instructorOnly={true}>
            <InstructorSubmissions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/institution"
        element={
          <ProtectedRoute>
            <InstitutionDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/book-training"
        element={
          <ProtectedRoute instructorOnly={true}>
            <BookTraining />
          </ProtectedRoute>
        }
      />
      <Route
        path="/request-case"
        element={
          <ProtectedRoute instructorOnly={true}>
            <RequestCase />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AppRoutes />
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;